import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';
import { initialMessage, userMessage } from 'constants/ui.constants';
import {
	getWebhookMonitoringAnalytics,
	getWebhookMonitoringHealthcheck,
	getWebhookMonitoringOperationDetails,
	getWebhookMonitoringOperations,
} from 'services/webhook-monitoring.service';

type WebhookMonitoringCard = {
	total: number;
	today: number;
};

type WebhookMonitoringTableState = {
	data: any[];
	pagination: { total: number; hasMore: boolean };
	loading: boolean;
	initialLoading: boolean;
};

type WebhookMonitoringDetailsState = {
	data: any;
	loading: boolean;
	shouldCloseAfterFailure: boolean;
	operationId: string | null;
};

interface IWebhookMonitoringState {
	analytics: {
		pendingAndInProgress: WebhookMonitoringCard;
		completedSuccessfully: WebhookMonitoringCard;
		deadLetter: WebhookMonitoringCard;
		loading: boolean;
	};
	table: WebhookMonitoringTableState;
	details: WebhookMonitoringDetailsState;
	health: {
		data: any;
		loading: boolean;
	};
	error: userMessage;
}

const defaultCard = { total: 0, today: 0 };

const defaultTableState = {
	data: [],
	pagination: { total: 0, hasMore: false },
	loading: false,
	initialLoading: false,
};

const defaultDetailsState = {
	data: null,
	loading: false,
	shouldCloseAfterFailure: false,
	operationId: null,
};

const initialState: IWebhookMonitoringState = {
	analytics: {
		pendingAndInProgress: defaultCard,
		completedSuccessfully: defaultCard,
		deadLetter: defaultCard,
		loading: false,
	},
	table: { ...defaultTableState },
	details: { ...defaultDetailsState },
	health: {
		data: null,
		loading: false,
	},
	error: initialMessage,
};

const webhookMonitoringSlice = createSlice({
	name: 'webhookMonitoring',
	initialState,
	reducers: {
		setError: (state, action: PayloadAction<userMessage>) => {
			state.error = action.payload;
		},
		clearError: (state) => {
			state.error = initialMessage;
		},
		clearDetails: (state) => {
			state.details = { ...defaultDetailsState };
		},
		clearTable: (state) => {
			state.table = { ...defaultTableState };
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(getWebhookMonitoringAnalytics.pending, (state) => {
				state.analytics.loading = true;
			})
			.addCase(getWebhookMonitoringAnalytics.fulfilled, (state, { payload }) => {
				state.analytics.loading = false;
				state.analytics.pendingAndInProgress = payload.pendingAndInProgress;
				state.analytics.completedSuccessfully = payload.completedSuccessfully;
				state.analytics.deadLetter = payload.deadLetter;
			})
			.addCase(getWebhookMonitoringAnalytics.rejected, (state) => {
				state.analytics.loading = false;
			})
			.addCase(getWebhookMonitoringOperations.pending, (state) => {
				if (state.table.data.length) {
					state.table.loading = true;
				} else {
					state.table.initialLoading = true;
				}
			})
			.addCase(getWebhookMonitoringOperations.fulfilled, (state, { payload }) => {
				state.table = {
					data: payload.result,
					pagination: payload.pagination,
					loading: false,
					initialLoading: false,
				};
			})
			.addCase(getWebhookMonitoringOperations.rejected, (state) => {
				state.table.loading = false;
				state.table.initialLoading = false;
			})
			.addCase(getWebhookMonitoringOperationDetails.pending, (state, { meta }) => {
				state.details.loading = true;
				state.details.shouldCloseAfterFailure = false;
				state.details.operationId = meta.arg.operationId;
			})
			.addCase(getWebhookMonitoringOperationDetails.fulfilled, (state, { meta, payload }) => {
				if (state.details.operationId !== meta.arg.operationId) {
					return;
				}

				state.details.loading = false;
				state.details.data = payload;
				state.details.shouldCloseAfterFailure = false;
			})
			.addCase(getWebhookMonitoringOperationDetails.rejected, (state, { meta }) => {
				if (state.details.operationId !== meta.arg.operationId) {
					return;
				}

				state.details.loading = false;
				state.details.data = null;
				state.details.shouldCloseAfterFailure = true;
			})
			.addCase(getWebhookMonitoringHealthcheck.pending, (state) => {
				state.health.loading = true;
			})
			.addCase(getWebhookMonitoringHealthcheck.fulfilled, (state, { payload }) => {
				state.health.loading = false;
				state.health.data = payload;
			})
			.addCase(getWebhookMonitoringHealthcheck.rejected, (state) => {
				state.health.loading = false;
			});
	},
});

export const getWebhookMonitoringAnalyticsData = (state: any) => state.webhookMonitoring.analytics;
export const getWebhookMonitoringError = (state: any) => state.webhookMonitoring.error;
export const getWebhookMonitoringDetailsState = (state: any) => state.webhookMonitoring.details;
export const getWebhookMonitoringHealthState = (state: any) => state.webhookMonitoring.health;
export const getWebhookMonitoringTableState = (state: any): WebhookMonitoringTableState =>
	state.webhookMonitoring.table;

export const selectWebhookMonitoringTableAndDetails = createSelector(
	[(state: any) => getWebhookMonitoringTableState(state), (state: any) => getWebhookMonitoringDetailsState(state)],
	(table, details) => ({ table, details })
);

export const { setError, clearError, clearDetails, clearTable } = webhookMonitoringSlice.actions;

export default webhookMonitoringSlice.reducer;
